import {
  APP_ANALYTICS_ALLOWED_PROPERTY_KEYS,
  APP_ANALYTICS_EVENT_NAMES,
  APP_ANALYTICS_IDENTIFY_PROPERTY_KEYS,
  type AppAnalytics,
  type AppAnalyticsEventMap,
  type AppAnalyticsEventName,
  type AppAnalyticsPlatform,
  type AppAnalyticsProvider,
  type AppAnalyticsUserProperties,
  type AppScreenProperties,
} from './appAnalyticsTypes';

const KNOWN_EVENTS = new Set<string>(APP_ANALYTICS_EVENT_NAMES);

type Primitive = string | number | boolean;

function isPrimitive(value: unknown): value is Primitive {
  return (
    typeof value === 'string' ||
    (typeof value === 'number' && Number.isFinite(value)) ||
    typeof value === 'boolean'
  );
}

/** Event param strings stay short enums / ids. */
const MAX_EVENT_STRING_LENGTH = 100;
/** GA4 user property value limit. */
const MAX_USER_PROPERTY_LENGTH = 36;

export function sanitizeAppAnalyticsProperties(
  properties: unknown,
  platform: AppAnalyticsPlatform,
): Record<string, Primitive> {
  const sanitized: Record<string, Primitive> = { platform };
  if (properties && typeof properties === 'object') {
    for (const [key, value] of Object.entries(
      properties as Record<string, unknown>,
    )) {
      if (!APP_ANALYTICS_ALLOWED_PROPERTY_KEYS.has(key)) continue;
      if (value === undefined || value === null) continue;
      if (!isPrimitive(value)) continue;
      if (typeof value === 'string' && value.length > MAX_EVENT_STRING_LENGTH) {
        sanitized[key] = value.slice(0, MAX_EVENT_STRING_LENGTH);
        continue;
      }
      sanitized[key] = value;
    }
  }
  sanitized.platform = platform;
  return sanitized;
}

export function sanitizeIdentifyProperties(
  properties: AppAnalyticsUserProperties,
): { userId: string; userProperties: Record<string, string> } {
  const userId = String(properties.user_id || '').slice(
    0,
    MAX_USER_PROPERTY_LENGTH,
  );
  const userProperties: Record<string, string> = {};
  for (const key of APP_ANALYTICS_IDENTIFY_PROPERTY_KEYS) {
    const value = properties[key as keyof AppAnalyticsUserProperties];
    if (value === undefined || value === null || value === '') continue;
    userProperties[key] = String(value).slice(0, MAX_USER_PROPERTY_LENGTH);
  }
  return { userId, userProperties };
}

function isolateProviderResult(result: unknown): void {
  if (result && typeof (result as PromiseLike<unknown>).then === 'function') {
    void Promise.resolve(result).catch(() => {});
  }
}

export function createAppAnalytics(
  provider: AppAnalyticsProvider | null,
  platform: AppAnalyticsPlatform,
  options?: { providerDeliversRemotely?: boolean },
): AppAnalytics {
  const deliversRemotely =
    provider !== null && options?.providerDeliversRemotely === true;

  return {
    providerDeliversRemotely: deliversRemotely,
    track<E extends AppAnalyticsEventName>(
      event: E,
      properties: AppAnalyticsEventMap[E],
    ): void {
      try {
        if (!KNOWN_EVENTS.has(event)) return;
        if (!provider) return;
        const sanitized = sanitizeAppAnalyticsProperties(properties, platform);
        isolateProviderResult(provider.logEvent(event, sanitized));
      } catch {
        // never block UX
      }
    },
    identify(properties: AppAnalyticsUserProperties): void {
      try {
        if (!provider || !properties?.user_id) return;
        const { userId, userProperties } =
          sanitizeIdentifyProperties(properties);
        if (!userId) return;
        if (provider.setUserId) {
          isolateProviderResult(provider.setUserId(userId));
        }
        if (
          provider.setUserProperties &&
          Object.keys(userProperties).length > 0
        ) {
          isolateProviderResult(provider.setUserProperties(userProperties));
        }
      } catch {
        // never block UX
      }
    },
    screen(screenName: string, properties?: AppScreenProperties): void {
      try {
        if (!provider || !screenName) return;
        const sanitized = sanitizeAppAnalyticsProperties(
          {
            screen_name: screenName,
            ...(properties || {}),
          },
          platform,
        );
        isolateProviderResult(provider.logEvent('app_screen_viewed', sanitized));
      } catch {
        // never block UX
      }
    },
  };
}
